import React, { useState, useEffect, useContext, useRef } from "react";
import { ThemeContext } from "../../context";
import "./productList.css";

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [screenSize, setScreenSize] = useState("lg");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const modalRef = useRef(null);
    const theme = useContext(ThemeContext);
    const darkMode = theme.state.darkMode;

    const localJsonFile = "/products.json";

    const openModal = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);
        document.body.style.overflow = '';
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
        if (isModalOpen) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isModalOpen]);

    // Determine number of projects to show based on screen size
    const getVisibleCount = () => {
        if (screenSize === "mobile") return 3;
        if (screenSize === "tablet") return 6;
        return 8;
    };

    useEffect(() => {
        fetchLocalData();
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleResize = () => {
        const width = window.innerWidth;
        if (width < 768) setScreenSize("mobile");
        else if (width < 1024) setScreenSize("tablet");
        else setScreenSize("lg");
    };

    const fetchLocalData = async () => {
        try {
            const response = await fetch(localJsonFile);
            if (!response.ok) throw new Error("Failed to fetch local JSON");

            const data = await response.json();
            const uniqueProducts = data.map((item, index) => ({ ...item, id: index + 1 }));
            setProducts(uniqueProducts);
        } catch (error) {
            console.error("Error fetching local JSON data:", error);
            setProducts([]);
        }
    };

    const VISIBLE_COUNT = getVisibleCount();
    const filteredProducts = products.filter(p => p.visible !== false);
    
    // Separate products by category
    const shippedProducts = filteredProducts.filter(p => p.category === "shipped");
    const prototypeProducts = filteredProducts.filter(p => p.category === "prototype");
    
    const visibleShipped = showAll ? shippedProducts : shippedProducts.slice(0, VISIBLE_COUNT);
    const visiblePrototype = showAll ? prototypeProducts : prototypeProducts.slice(0, VISIBLE_COUNT);

    // Create a short preview for list views
    const getPreview = (text, wordLimit = 12) => {
        if (!text) return "";
        const words = text.split(/\s+/).filter(Boolean);
        if (words.length <= wordLimit) return text;
        return words.slice(0, wordLimit).join(' ') + '...';
    };
    
    // Format genres array
    const formatGenres = (genres) => {
        if (Array.isArray(genres)) return genres.join(', ');
        return genres || '';
    };

    const ProjectCard = ({ product }) => (
        <div
            role="button"
            tabIndex={0}
            onClick={() => openModal(product)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(product); }}
            className="product rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer"
            style={{background: 'rgba(var(--bg-light), 0.03)', border: '1px solid rgba(var(--primary-accent), 0.06)'}}
        >
            <div className="product-img-container rounded-lg overflow-hidden">
                <img
                    src={product.img}
                    alt={product.title}
                    className="product-img w-full h-48 md:h-56 object-cover transition-transform duration-300 hover:scale-105"
                    loading="lazy"
                />
            </div>

            <div className="p-4 md:p-5">
                <h3 className="text-lg md:text-xl font-semibold" style={{color: 'rgb(var(--primary-accent))'}}>
                    {product.title}
                </h3>
                <div className="mt-2 text-xs md:text-sm text-gray-500 dark:text-gray-500 space-y-1">
                    <p>{formatGenres(product.genre)}</p>
                    <p className="text-gray-400 dark:text-gray-500">{product.platform}</p>
                </div>
                <p className="mt-3 text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed product-desc">{product.shortDescription || getPreview(product.description, 12)}</p>
            </div>
        </div>
    );

    return (
        <section className="product-list py-20 md:py-28 transition-colors duration-300" style={{background: 'rgba(var(--bg-dark),0.04)'}}>
            <div id="projects" className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                <div className="mb-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{color: 'rgb(var(--primary-accent))'}}>Projects & Games</h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400">A showcase of completed games and interactive experiences</p>
                </div>

                {/* Shipped Projects Section */}
                {shippedProducts.length > 0 && (
                    <div className="mb-16">
                        <h3 className="text-2xl md:text-3xl font-bold mb-6" style={{color: 'rgb(var(--primary-accent))'}}>
                            🚀 Released & Shipped
                        </h3>
                        <div className="products-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                            {visibleShipped.map((product) => (
                                <ProjectCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Prototype Projects Section */}
                {prototypeProducts.length > 0 && (
                    <div>
                        <h3 className="text-2xl md:text-3xl font-bold mb-6" style={{color: 'rgb(var(--primary-accent))'}}>
                            🔬 Prototypes & R&D
                        </h3>
                        <div className="products-container grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                            {visiblePrototype.map((product) => (
                                <ProjectCard key={product.id} product={product} />
                            ))}
                        </div>
                    </div>
                )}

                {(shippedProducts.length > VISIBLE_COUNT || prototypeProducts.length > VISIBLE_COUNT) && (
                    <div className="mt-12 text-center">
                        <button onClick={() => setShowAll(!showAll)} className="px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors duration-300">
                            {showAll ? "SHOW LESS" : "SHOW ALL"}
                        </button>
                    </div>
                )}

                {/* Modal */}
                {isModalOpen && selectedProduct && (
                    <div
                        className="lightbox-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={selectedProduct.title}
                        onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                        ref={modalRef}
                    >
                        <div className="lightbox-content modal-wrapper" style={{background: 'rgba(var(--bg-light), 0.01)', borderRadius: '12px', padding: '1rem', boxShadow: '0 10px 30px rgba(0,0,0,0.6)'}}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex flex-col gap-2">
                                    <h3 className="modal-title text-xl font-semibold" style={{color: 'rgb(var(--primary-accent))'}}>{selectedProduct.title}</h3>
                                    <div className="text-sm text-gray-500 dark:text-gray-400 space-x-2">
                                        <span className="inline-block px-2 py-1 rounded bg-opacity-20" style={{backgroundColor: 'rgb(var(--primary-accent))'}}>{selectedProduct.category === 'shipped' ? '🚀 Shipped' : '🔬 Prototype'}</span>
                                        <span className="text-gray-600 dark:text-gray-400">{selectedProduct.platform}</span>
                                        <span className="text-gray-600 dark:text-gray-400">{formatGenres(selectedProduct.genre)}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 items-start">
                                    {/* Only shipped projects can be tried */}
                                    {selectedProduct.category === 'shipped' && selectedProduct.link && (
                                        <a href={selectedProduct.link} target="_blank" rel="noopener noreferrer" className="modal-link-btn px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-200">
                                           Try It
                                        </a>
                                    )}
                                    <button className="lightbox-close-btn" onClick={closeModal} aria-label="Close">&times;</button>
                                </div>
                            </div>

                            {selectedProduct.img && (
                                <div className="modal-media mb-4">
                                    <img src={selectedProduct.img} alt={selectedProduct.title} className="modal-img rounded-md" />
                                </div>
                            )}

                            <div className="modal-body">
                                <div className="modal-desc text-gray-700 dark:text-gray-300">
                                    {selectedProduct.description}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProductList;
